import React, { useState, useEffect } from "react";
import { isEmpty } from "lodash";
import PropTypes from "prop-types";
import { useTheme } from "@/components/ThemeProvider";

import routeWithApiKeySession from "@/components/application-area/routeWithApiKeySession";
import Link from "@/components/Link";
import BigMessage from "@/components/BigMessage";
import PageHeader from "@/components/page-header";
import Parameters from "@/components/parameters/Parameters";
import DashboardGrid from "@/components/dashboards/DashboardGrid";
import Filters from "@/components/Filters";

import { Dashboard } from "@/services/dashboard";
import routes from "@/services/routes";

import logoUrl from "@/assets/images/redash_icon_small.png";
import useDashboard from "./hooks/useDashboard";
import "./PublicDashboardPage.less";


function PublicDashboard({ dashboard, isDarkMode }) {
  const { globalParameters, filters, setFilters, refreshDashboard, loadWidget, refreshWidget } = useDashboard(
    dashboard
  );

  return (
    <div className={`container p-t-10 p-b-20 ${isDarkMode ? 'dark-mode' : ''}`}>
      <PageHeader title={dashboard.name} />
      {!isEmpty(globalParameters) && (
        <div className={`m-b-10 p-15 ${isDarkMode ? 'bg-dark' : 'bg-white'} tiled`}>
          <Parameters parameters={globalParameters} onValuesChange={refreshDashboard} />
        </div>
      )}
      {!isEmpty(filters) && (
        <div className={`m-b-10 p-15 ${isDarkMode ? 'bg-dark' : 'bg-white'} tiled`}>
          <Filters filters={filters} onChange={setFilters} />
        </div>
      )}
      <div id="dashboard-container" className={isDarkMode ? 'dark-mode' : ''}>
        <DashboardGrid
          dashboard={dashboard}
          widgets={dashboard.widgets}
          filters={filters}
          isEditing={false}
          isPublic
          onLoadWidget={loadWidget}
          onRefreshWidget={refreshWidget}
        />
      </div>
    </div>
  );
}

PublicDashboard.propTypes = {
  dashboard: PropTypes.object.isRequired,
  isDarkMode: PropTypes.bool.isRequired,
};

function PublicDashboardPage({ token, onError }) {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    Dashboard.getByToken({ token })
      .then(dashboardData => {
        setDashboard(dashboardData);
        setLoading(false);
      })
      .catch(error => {
        setLoading(false);
        onError(error);
      });
  }, [token, onError]);

  if (loading) {
    return (
      <div className={`public-dashboard-page ${isDarkMode ? 'dark-mode' : ''}`}>
        <div className={`container loading-message ${isDarkMode ? 'bg-dark' : ''}`}>
          <BigMessage className="" icon="fa-spinner fa-2x fa-pulse" message="Loading..." />
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return null; // The error will be handled by the onError callback
  }

  return (
    <div className={`public-dashboard-page ${isDarkMode ? 'dark-mode' : ''}`}>
      <PublicDashboard dashboard={dashboard} isDarkMode={isDarkMode} />
      <div id="footer" className={isDarkMode ? 'bg-dark' : ''}>
        <div className="text-center">
          <Link href="https://inverse.finance">
            <img alt="Inverse Logo" src={logoUrl} width="38" />
          </Link>
        </div>
        <Link href="https://inverse.finance/" target="_blank" rel="noopener noreferrer">
          Inverse Finance
        </Link>
      </div>
    </div>
  );
}

PublicDashboardPage.propTypes = {
  token: PropTypes.string.isRequired,
  onError: PropTypes.func,
};

PublicDashboardPage.defaultProps = {
  onError: () => {},
};

routes.register(
  "Dashboards.ViewShared",
  routeWithApiKeySession({
    path: "/public/dashboards/:token",
    render: pageProps => <PublicDashboardPage {...pageProps} />,
    getApiKey: currentRoute => currentRoute.routeParams.token,
  })
);

export default PublicDashboardPage;
